/**
 * The lab: pick a piece, pick who plays it, and set by hand every choice arranging has.
 *
 * Arrange (`ArrangePage`) asks for a score, its players and a wish in words, and chooses the
 * rest; this board is where each of those choices is a control of its own, for whoever is
 * finding out what the arranger does with them. It is the other direction of the same machine.
 * Write starts from a progression typed by hand; this board starts from a score somebody
 * already wrote and hands its own notes to the players
 * picked — each of its staves whole where there is a player for every one, or else the tune,
 * the bass and the notes between — each where the source wrote it, moved by an octave only
 * where a player cannot reach it. Nothing is transcribed and nothing is added: every note
 * comes out of the file, and a player the music gives nothing to rests. Anything more is a
 * choice asked for under Harmony or Options (`ArrangeChoices`), none made until it is, and the reading bar
 * counts what it added apart from the source's notes. Arranging can also start from the tune
 * (`ArrangeStartMenu`): only the score's tune and chords are kept and every other part is written
 * again, so the run is sent with none of those choices and no check, whatever is set for when the
 * score is started from again, and its result is read by `TunedReading` with no offers; or from
 * the note model, which writes the parts under the tune and is read by `ArrangeReading`, its
 * notes counted as added, with no offers either. Where following the tune down or the
 * bass up would give a mostly silent line its notes, the strip above the reading bar offers
 * it (`ArrangeOffers`), and what a player reading a part would call wrong is said above that
 * (`ArrangeIdiom`). Under the toolbar, the roster and every setting on it can be asked for
 * in words (`ArrangeAsk`), which arranges what it sets at once, a long window's first bars first
 * with the rest offered under it (`ArrangeContinue`, `lib/ask-run`). The strip stays when a run
 * folds the toolbar away, since asking again after hearing a run is what it is for.
 *
 * The board is a window rather than a page: a toolbar at the top, the score desks in the
 * middle taking whatever height is left, and who plays what along the bottom. Only
 * the desks scroll, so the two things a run is judged by — the settings that produced it and
 * the verdict on it — never leave the screen while the music is being looked at.
 *
 * The result is parsed here rather than in the audition panel, because the panel's hooks want
 * a score and a hook cannot be skipped when a parse fails. A file the editor's importer will
 * not read is a bug worth seeing, so the reason is printed instead of being swallowed.
 *
 * Both sides are mounted: the bars of the upload, and what the arranger wrote over them.
 * Hearing one after the other is the only way to tell what the new players changed, and the
 * original is cut to the same window so that the comparison is honest. They sit side
 * by side where the window is wide enough for two, because that is the comparison drawn as
 * well as heard, and one at a time with a switch where it is not (`ArrangeDesks`). Both draw at most `DRAWN_BARS` from the start of it: a whole orchestral
 * movement engraved at once froze the board for half a minute.
 *
 * The upload is drawn as soon as it is picked, before anything is arranged. Until
 * it is, the board gives no sign that the right file and the right bars went in, and a run
 * that comes back thin is impossible to tell from a file that was never read. Its window is
 * the whole piece, or the one typed into the form once Whole piece is cleared (`ArrangeBars`),
 * and narrows to the bars the reader reports once it has run.
 *
 * A score can also arrive named by its id in the address (`/lab?score=<id>`), as a
 * library score reaches Arrange. It is fetched and drawn as an upload is, but arranged by its id:
 * the server reads it from its own disk rather than having it sent back. It carries its
 * credit line under the toolbar until another file is opened in its place.
 *
 * A run is a job on the server, followed by `lib/arrange-job.ts`: a whole movement takes
 * tens of seconds, and while it runs the strip under the toolbar says what the pipeline is doing and
 * offers to stop it.
 *
 * A file is opened by `lib/arrange-open.ts`. A MIDI file is written down as MusicXML and a PDF
 * of printed music is read on the server, as on Arrange, under the same strip a run is followed
 * by; the note that says so stays under the toolbar for as long as that file is open: what is
 * drawn as the original is a reading of the file, not the file's own.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { AskPayload, LibraryEntry } from "@app/shared-types";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import ArrangeAsk from "../components/ArrangeAsk";
import ArrangeContinue from "../components/ArrangeContinue";
import { type Choices, NO_CHOICES } from "../components/ArrangeChoices";
import ArrangeDesks, { labWaiting } from "../components/ArrangeDesks";
import ArrangeIdiom from "../components/ArrangeIdiom";
import ArrangeOffers from "../components/ArrangeOffers";
import ArrangeProgress from "../components/ArrangeProgress";
import ArrangeReading from "../components/ArrangeReading";
import { NO_REVIEW } from "../components/ArrangeReview";
import ArrangeToolbar, { type Picked } from "../components/ArrangeToolbar";
import { Trouble } from "../components/Chrome";
import TunedReading from "../components/TunedReading";
import LibraryCredit from "../components/LibraryCredit";
import { useArrangeJob } from "../lib/arrange-job";
import { useOpenScore } from "../lib/arrange-open";
import { fileName, SCORE_PARAM, useLibraryScore } from "../lib/library";
import {
  arrangingOf,
  arrangingSaid,
  askedWindow,
  barsSaid,
  type Board,
  boardOf,
  heldBack,
  type Ran,
  requestOf,
  settingsOf,
} from "../lib/ask-run";
import { barsOf, DRAWN_BARS, original, parsed } from "../lib/score-window";

export default function LabPage(): JSX.Element {
  const navigate = useNavigate();
  const seats = useAsync("seats", () => api.seats());

  const [picked, setPicked] = useState<Picked | null>(null);
  // Every bar unless a window is asked for: arranging a piece means arranging all of it. The
  // fields keep 1–16 for the reader who clears the box to try a few bars first.
  const [allBars, setAllBars] = useState(true);
  const [first, setFirst] = useState("1");
  const [last, setLast] = useState("16");
  // No roster is chosen for the reader. A board that opens with four strings already seated
  // answers the one question it exists to ask, and a reader who presses Arrange without
  // noticing gets a string quartet they never picked.
  const [roster, setRoster] = useState<string[]>([]);
  const [choices, setChoices] = useState(NO_CHOICES);
  // The AI arranger's level and the check are kept apart from the rules arranger's choices: an
  // answer in words sets those, where arranging starts and what the note model is told, and
  // leaves the level and the check as they were.
  const [review, setReview] = useState(NO_REVIEW);
  // The take of the run last asked for: 1 from Arrange, one more from Another take.
  const [take, setTake] = useState(1);
  const job = useArrangeJob();
  const { result, error } = job;
  // Folded as a run comes back and unfolded as it is cleared: see `ArrangeToolbar`.
  const [folded, setFolded] = useState(false);
  useEffect(() => setFolded(result !== null), [result]);

  const [params, setParams] = useSearchParams();
  const wanted = params.get(SCORE_PARAM);
  const [credit, setCredit] = useState<LibraryEntry | null>(null);
  const library = useLibraryScore(wanted, ({ entry, text }) => {
    setPicked({ name: fileName(entry), text, note: null });
    setCredit(entry);
    job.clear();
  });

  const opening = useOpenScore((next) => {
    // The reading and both panels belong to the file they were made from, and the library's
    // credit and its id in the address to the library score this file replaces.
    setPicked(next);
    job.clear();
    setCredit(null);
    if (wanted !== null) setParams({}, { replace: true });
  });
  // A scan being read and a run are said by one strip, the scan first.
  const busy = opening.progress ?? job.progress;

  const musicxml = result?.musicxml ?? null;
  const read = useMemo(() => parsed(musicxml), [musicxml]);
  const arranged = useMemo(() => read.score && barsOf(read.score, 1, DRAWN_BARS).score, [read]);
  // Before a run the window is the one typed into the toolbar; after one it is the window the
  // reader reports, which is the typed one clipped to the bars the file actually has.
  const board: Board = { roster, choices, review, whole: allBars, first, last };
  const asked = askedWindow(board);
  const from = result?.source.first ?? asked?.[0] ?? 1;
  const to = result?.source.last ?? asked?.[1] ?? 100000;
  // Read once per score, not once per bar typed: a whole symphony takes most of a second.
  const whole = useMemo(() => parsed(picked?.text ?? null), [picked]);
  const source = useMemo(
    () => original(picked?.name ?? null, whole, from, to),
    [picked, whole, from, to],
  );

  // The score as a request in words is read against, and the run on screen.
  const opened = whole.score;
  const total = opened?.measures.length ?? null;
  const facts = opened && { bars: opened.measures.length, parts: opened.parts.map((p) => p.name) };
  const [ran, setRan] = useState<Ran | null>(null);
  const setBoard = (next: Board): void => {
    setRoster([...next.roster]);
    setChoices(next.choices);
    setReview(next.review);
    setAllBars(next.whole);
    setFirst(next.first);
    setLast(next.last);
  };

  const launch = (next: Ran): void => {
    if (picked === null || next.board.roster.length === 0) return;
    setTake(next.take);
    setRan(next);
    job.run(requestOf(next, picked, credit?.id ?? null));
  };
  // `next` is the choices to run with when an offer ticks one, since the state set with it is
  // not read until the next render.
  const run = (next: Choices = choices, nextTake = 1): void => {
    const at = { ...board, choices: next };
    setChoices(next);
    launch({ board: at, bars: askedWindow(at), take: nextTake, preview: false });
  };
  // An answer in words sets the board and arranges what it asks for (`lib/ask-run`), and says so.
  const answered = (answer: AskPayload): string | null => {
    const next = boardOf(board, answer);
    setBoard(next);
    const held = heldBack(next, picked !== null);
    if (held !== null) return held;
    const arranging = arrangingOf(board, next, result === null ? null : ran, answer.unmet, total);
    if (arranging !== null) launch(arranging);
    return arranging && arrangingSaid(arranging, total);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ArrangeToolbar
        picked={picked}
        whole={allBars}
        first={first}
        last={last}
        total={total}
        roster={roster}
        seats={seats.data?.seats ?? []}
        running={job.progress !== null}
        choices={choices}
        review={review}
        folded={folded}
        foldable={result !== null}
        onFold={setFolded}
        onOpen={opening.open}
        onWhole={setAllBars}
        onWindow={(nextFirst, nextLast) => setBoard({ ...board, first: nextFirst, last: nextLast })}
        onRoster={(next) => setBoard({ ...board, roster: next })}
        onChoices={setChoices}
        onReview={setReview}
        onRun={() => run()}
        take={take}
        onAnother={() => run(choices, take + 1)}
      />

      <ArrangeAsk
        roster={roster}
        settings={settingsOf(board, total)}
        score={facts}
        seats={seats.data?.seats ?? []}
        onAnswer={answered}
      />
      {busy === null ? null : (
        <ArrangeProgress
          progress={busy}
          onCancel={opening.progress === null ? job.cancel : opening.cancel}
        />
      )}
      {result === null || ran === null || !ran.preview || ran.bars === undefined ? null : (
        <ArrangeContinue
          heard={ran.bars}
          rest={barsSaid(askedWindow(ran.board), total)}
          onContinue={() => launch({ ...ran, bars: askedWindow(ran.board), preview: false })}
        />
      )}
      {credit === null ? null : <LibraryCredit entry={credit} />}
      {picked?.note == null ? null : (
        <p className="shrink-0 border-b border-shell-edge bg-shell-bar px-2.5 py-1 text-[11px] text-shell-dim">
          {picked.note}
        </p>
      )}
      {library.error === null ? null : (
        <Trouble>
          The library could not hand over {wanted}: {library.error}
        </Trouble>
      )}
      {seats.error === null ? null : (
        <Trouble>
          The pipeline did not answer when asked which seats it knows: {seats.error}
        </Trouble>
      )}
      {error === null ? null : <Trouble>{error}</Trouble>}

      <ArrangeDesks
        wanted={wanted}
        fetching={library.loading}
        source={source}
        waiting={labWaiting(picked !== null, roster.length > 0, review.start)}
        beside
        result={result}
        read={read}
        arranged={arranged}
        growing={opening.ready !== null}
        onOpenInEditor={(text) => navigate("/editor", { state: { musicxml: text } })}
      />

      {result === null || result.start === "tune" ? null : <ArrangeIdiom idiom={result.idiom} />}

      {result?.start !== "score" ? null : (
        <ArrangeOffers
          offers={result.offers}
          // An offer ticks a choice, and only starting from the score sends them.
          disabled={
            job.progress !== null ||
            picked === null ||
            roster.length === 0 ||
            review.start !== "score"
          }
          onTake={(choice) => run({ ...choices, [choice]: true })}
        />
      )}
      {result === null ? null : result.start === "tune" ? (
        <TunedReading result={result} />
      ) : (
        <ArrangeReading result={result} />
      )}
    </div>
  );
}
